import { Box, IconButton, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { dateTimeFormatLong } from "../../../common/helpers/formatDateTime";
import { collectConversationAttachments } from "../attachments/messageAttachments";
import { ConversationAttachment } from "../attachments/types";
import { openConversationAttachment } from "../messagingActions";
import { ChatMessage } from "../types/queries";
import MessageAttachmentChip from "./MessageAttachmentChip";

type Props = {
  messages: ChatMessage[];
  compact?: boolean;
};

const ConversationAttachmentsList = ({ messages, compact = false }: Props) => {
  const { t } = useTranslation("messaging");
  const dispatch = useAppDispatch();
  const attachments = collectConversationAttachments(messages).filter(
    (attachment) => attachment.status === "ready" && attachment.pathspec,
  );

  const downloadAttachment = (attachment: ConversationAttachment) => {
    if (!attachment.pathspec || !attachment.conversationId) return;
    void openConversationAttachment(dispatch, {
      conversationId: attachment.conversationId,
      messageId: attachment.messageId,
      pathspec: attachment.pathspec,
    });
  };
  const contentPx = compact ? 1.5 : 2;

  if (!attachments.length) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: contentPx,
        }}
      >
        <Typography color="text.secondary">{t("noAttachments")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      {attachments.map((attachment) => (
        <Box
          key={`${attachment.messageId}-${attachment.id}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
            px: contentPx,
            borderBottom: "1px solid #E3E4E7",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MessageAttachmentChip attachment={attachment} compact={compact} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "12px", pl: compact ? "28px" : "32px" }}
            >
              {format(
                parseISO(attachment.sentAt),
                dateTimeFormatLong.format,
                dateTimeFormatLong.locale,
              )}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={t("downloadAttachment")}
            onClick={() => downloadAttachment(attachment)}
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

export default ConversationAttachmentsList;
